import { useEffect, useState } from "react";
import { format } from "date-fns";
import axiosInstance from "../../api/axiosInstance";
import Select from "react-select";
import Loader from "../../common/Loader";

const SiteReport = () => {
  const today = format(new Date(), "yyyy-MM-dd");
  const [fromDate, setFromDate] = useState("2024-01-01");
  const [toDate, setToDate] = useState("2025-12-31");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [salePersonData, setSalePersonData] = useState([]);
  const [selectedSalePerson, setSelectedSalePerson] = useState(null);

  const fetchSalePersons = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("User/GetAllUser");
      setSalePersonData(response.data);
    } catch (error) {
      console.error("Failed to fetch report:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post("Report/GetFilteredNewDeals", {
        fromDate,
        toDate,
        sale_Person_Id: selectedSalePerson
          ? parseInt(selectedSalePerson.value)
          : null,
      });
      setReportData(response.data);
    } catch (error) {
      console.error("Failed to fetch report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    fetchSalePersons();
  }, []);

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "0.75rem",
      borderColor: state.isFocused ? "#405189" : "#e2e8f0",
      padding: "2px",
      minHeight: "44px",
      backgroundColor: "#f8fafc80",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(64, 81, 137, 0.1)" : "none",
      "&:hover": { borderColor: "#cbd5e1" },
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "0.75rem",
      color: "#94a3b8",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.05em"
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "0.875rem",
      color: "#334155",
      fontWeight: "600",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "1rem",
      border: "1px solid #f1f5f9",
      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
      zIndex: 50,
      padding: "8px"
    }),
    option: (base, state) => ({
      ...base,
      borderRadius: "0.5rem",
      backgroundColor: state.isSelected ? "#405189" : state.isFocused ? "#f1f5f9" : "transparent",
      color: state.isSelected ? "white" : "#475569",
      fontSize: "0.80rem",
      fontWeight: "600",
      padding: "10px 12px",
      cursor: "pointer",
      transition: "all 0.2s"
    })
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Sites Report</h2>
        <span className="bg-white border border-slate-200 px-4 py-1.5 rounded-full text-[10px] font-black text-slate-500 uppercase tracking-widest shadow-sm">
          {reportData.length} Records Found
        </span>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
        <div className="flex flex-col gap-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all outline-none h-[44px]"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-full px-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all outline-none h-[44px]"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Sale Person</label>
          <Select
            options={salePersonData.map((per) => ({
              value: String(per.sale_Person_Id),
              label: per.Sale_Person_Name,
            }))}
            value={selectedSalePerson}
            onChange={setSelectedSalePerson}
            isClearable
            styles={customSelectStyles}
            placeholder="SELECT AGENT"
          />
        </div>

        <button
          onClick={fetchReport}
          className="w-full h-[44px] bg-primary text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-primary-dark hover:shadow-lg hover:-translate-y-0.5 transition-all active:scale-95 shadow-md shadow-primary/20 flex items-center justify-center gap-2"
        >
          <span>Search Reports</span>
        </button>
      </div>

      {/* Report Table */}
      {loading ? (
        <div className="min-h-[400px] flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Site Name</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Site Type</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Contact person</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Site Address</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Owner Name</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Site Location</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Architect Name</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Sale Person</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {reportData.length > 0 ? (
                  reportData.map((item, index) => (
                    <tr key={index} className="hover:bg-slate-50/30 transition-colors group">
                      <td className="px-6 py-4 text-xs font-bold text-slate-700 whitespace-nowrap">{item.site_Name}</td>
                      <td className="px-6 py-4 text-xs font-bold text-slate-600 whitespace-nowrap">{item.site_Type}</td>
                      <td className="px-6 py-4 text-xs font-bold text-slate-600 whitespace-nowrap">{item.contact_Person}</td>
                      <td className="px-6 py-4 text-xs font-bold text-slate-600 min-w-[200px]" title={item.site_Address}>{item.site_Address}</td>
                      <td className="px-6 py-4 text-xs font-bold text-slate-600 whitespace-nowrap">{item.owner_Name}</td>
                      <td className="px-6 py-4 text-xs font-bold text-primary underline cursor-pointer whitespace-nowrap">
                        <a href={item.site_Location_Link} target="_blank" rel="noopener noreferrer">View Map</a>
                      </td>
                      <td className="px-6 py-4 text-xs font-bold text-slate-600 whitespace-nowrap">{item.architect_Name || "-"}</td>
                      <td className="px-6 py-4 text-xs font-bold text-slate-600 whitespace-nowrap">{item.salesPerson_Name}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <span className="text-4xl">🔍</span>
                        <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">No records found</p>
                        <p className="text-slate-400 text-[10px]">Try adjusting your search criteria</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteReport;
